/**
 * St. Patrick's backdrop: a bright, festive scene — puffy clouds, two swags of
 * triangular flag bunting (green/cream/gold/orange) strung across the top, and a
 * strip of grass along the bottom. Behind content, `pointer-events: none`. Scene
 * colors come from theme vars (`--cloud`, `--grass`, `--bunt-*`); the sky
 * gradient itself is on the theme `body`. The falling shamrocks are a separate
 * ambient effect that finishes the party like confetti.
 */
import React from 'react';

import { Z } from '@shared/constants/zIndex';

const BUNT_COLORS = [
  'var(--bunt-green)',
  'var(--bunt-cream)',
  'var(--bunt-gold)',
  'var(--bunt-orange)',
];

/** Pick a bunting color by (cycling) index — always returns a string. */
const colorAt = (i: number): string =>
  BUNT_COLORS[i % BUNT_COLORS.length] ?? BUNT_COLORS[0] ?? 'var(--bunt-green)';

// Pennant centers across the sagging string (viewBox 0 0 400 40).
const PENNANT_CENTERS = [12, 40, 68, 96, 124, 152, 180, 208, 236, 264, 292, 320, 348, 376];
const STRING_D = 'M0,7 Q200,21 400,7';
const HALF = 8; // half pennant width
const HEADER = 3; // folded header tape that wraps the string
const DEPTH = 20; // pennant length

/** y of the sagging string at x (matches STRING_D's quadratic). */
const stringY = (x: number): number => 7 + 28 * (x / 400) * (1 - x / 400);

/**
 * One cloth pennant hung from the string: a folded header tape straddling the
 * line (darkened by the string color), the colored body, and a soft sheen down
 * the middle. The two top corners sit on the string so it reads as attached.
 */
const Pennant: React.FC<{ cx: number; color: string }> = ({ cx, color }) => {
  const topL = stringY(cx - HALF);
  const topR = stringY(cx + HALF);
  const bodyL = topL + HEADER;
  const bodyR = topR + HEADER;
  const apex = Math.max(bodyL, bodyR) + DEPTH;
  const header = `${cx - HALF},${topL} ${cx + HALF},${topR} ${cx + HALF},${bodyR} ${cx - HALF},${bodyL}`;
  return (
    <>
      <polygon points={header} fill={color} />
      <polygon points={header} fill="var(--bunt-string)" fillOpacity={0.28} />
      <path d={`M${cx - HALF},${bodyL} L${cx + HALF},${bodyR} L${cx},${apex} Z`} fill={color} />
      <path
        d={`M${cx - 4},${bodyL + 0.5} L${cx},${apex - 1} L${cx - 1.5},${bodyL + 0.6} Z`}
        fill="var(--cloud)"
        fillOpacity={0.16}
      />
    </>
  );
};

/** One swag of festive bunting, color cycle shifted by `offset`. */
const Bunting: React.FC<{ top: string; offset: number }> = ({ top, offset }) => (
  <svg
    className="absolute left-0 w-full"
    style={{ top, height: '16%' }}
    viewBox="0 0 400 40"
    preserveAspectRatio="none"
  >
    {PENNANT_CENTERS.map((cx, i) => (
      <Pennant key={cx} cx={cx} color={colorAt(i + offset)} />
    ))}
    {/* string drawn last so it threads over the folded headers */}
    <path d={STRING_D} fill="none" stroke="var(--bunt-string)" strokeWidth={1.6} />
  </svg>
);

/** A single puffy cloud. */
const Cloud: React.FC<{ style: React.CSSProperties }> = ({ style }) => (
  <svg className="absolute" style={style} viewBox="0 0 100 50" preserveAspectRatio="xMidYMid meet">
    <g fill="var(--cloud)">
      <ellipse cx="30" cy="34" rx="24" ry="16" />
      <ellipse cx="54" cy="28" rx="22" ry="18" />
      <ellipse cx="74" cy="36" rx="20" ry="14" />
      <rect x="22" y="36" width="58" height="14" />
    </g>
  </svg>
);

/** Fixed festival-bunting backdrop for the St. Patrick's theme. */
export const StPatricksBackdrop: React.FC = () => (
  <div
    aria-hidden
    data-testid="st-patricks-backdrop"
    className="pointer-events-none fixed inset-0"
    style={{ zIndex: Z.BACKDROP }}
  >
    <Cloud style={{ top: '8%', left: '6%', width: '18%', height: '13%' }} />
    <Cloud style={{ top: '16%', left: '70%', width: '22%', height: '15%' }} />
    <Cloud style={{ top: '30%', left: '40%', width: '15%', height: '11%' }} />

    <Bunting top="2%" offset={0} />
    <Bunting top="13%" offset={2} />

    {/* grass strip along the bottom */}
    <svg
      className="absolute bottom-0 left-0 w-full"
      style={{ height: '14%' }}
      viewBox="0 0 400 40"
      preserveAspectRatio="none"
    >
      <path d="M0,40 L0,16 Q120,4 250,12 Q340,18 400,8 L400,40 Z" fill="var(--grass)" />
    </svg>
  </div>
);
